import { InputBase, Tooltip as MuiTooltip } from '@mui/material'
import { styled, keyframes } from '@mui/system'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded'
import checkboxActiveMIcon from '../../../../../../assets/checkbox_active_m.svg'
import checkboxInactiveMIcon from '../../../../../../assets/checkbox_inactive_m.svg'
import { SelectOption } from '../../../../../model/selectOption'
import { colorPalette } from '../../../../../style/colorPalette'
import { intl } from '../../../../../../i18n'
import {
  buildTree,
  countTreeNodes,
  filterTree,
  TreeSelectOption,
} from '../../../../../features/ProcessScopeSearchSelectPopper/treeUtils'

// sticky top 計算用の行高さ（padding除去後）
const ROW_HEIGHT = 26

type Props = {
  options: SelectOption[]
  parentMap: Record<string, string>
  value: string[]
  onChange: (value: string[]) => void
  multiSelect?: boolean
  height?: string
  loading?: boolean
  placeholder?: string
}

// wbsItemSerachFilterPanel の ProcessScopeSearchSelectPopper 内で使用する、ツリー構造のセレクトコンポーネント
export const InlineTreeSelect = ({
  options,
  parentMap,
  value,
  onChange,
  multiSelect = true,
  height = '400px',
  loading = false,
  placeholder = intl.formatMessage({
    id: 'wbsItemSearchFilterPanel.processScope.searchPlaceholder',
  }),
}: Props) => {
  const selectedValues = useMemo(() => new Set(value), [value])

  const [filterText, setFilterText] = useState('')
  // 既定で全ノードを展開。ユーザーが明示的に畳んだものだけを記録する
  // （options の参照が変わっても展開状態が壊れず、初期描画から展開済みになる）
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set())
  const [ready, setReady] = useState(false)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    const timerId = setTimeout(() => setReady(true), 0)
    return () => {
      clearTimeout(timerId)
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])

  const handleSearchTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(() => {
        setFilterText(v)
        // 絞り込み結果を見えるように全展開する
        setCollapsedNodes(new Set())
      }, 300)
    },
    []
  )

  const toggleExpand = useCallback((e: React.MouseEvent, v: string) => {
    e.stopPropagation()
    setCollapsedNodes(prev => {
      const next = new Set(prev)
      if (next.has(v)) {
        next.delete(v)
      } else {
        next.add(v)
      }
      return next
    })
  }, [])

  const valueRef = useRef(value)
  valueRef.current = value
  const handleToggleItem = useCallback(
    (itemValue: string) => {
      if (!multiSelect) {
        onChange(valueRef.current.includes(itemValue) ? [] : [itemValue])
        return
      }
      const current = new Set(valueRef.current)
      if (current.has(itemValue)) {
        current.delete(itemValue)
      } else {
        current.add(itemValue)
      }
      onChange(Array.from(current))
    },
    [onChange, multiSelect]
  )

  const tree = useMemo(
    () => buildTree(options, parentMap),
    [options, parentMap]
  )

  const filteredTree = useMemo(
    () => filterTree(tree, filterText),
    [tree, filterText]
  )

  const filteredCount = useMemo(
    () => countTreeNodes(filteredTree),
    [filteredTree]
  )

  const totalCount = useMemo(() => countTreeNodes(tree), [tree])

  const renderTreeNodes = (
    nodes: TreeSelectOption[],
    depth: number
  ): React.ReactNode =>
    nodes.map(node => {
      const isSelected = selectedValues.has(node.value)
      const isExpanded = !collapsedNodes.has(node.value)
      return (
        <div key={node.value}>
          <TreeNodeRow
            node={node}
            depth={depth}
            isExpanded={isExpanded}
            isSelected={isSelected}
            onToggleItem={handleToggleItem}
            onToggleExpand={toggleExpand}
          />
          {node.children.length > 0 &&
            isExpanded &&
            renderTreeNodes(node.children, depth + 1)}
        </div>
      )
    })

  return (
    <Container style={{ height }}>
      <SearchInput
        placeholder={placeholder}
        onChange={handleSearchTextChange}
      />
      <OptionsList>
        {!ready || loading ? (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        ) : (
          renderTreeNodes(filteredTree, 0)
        )}
      </OptionsList>
      <FooterContainer>
        <CountLabel>
          <CountNumerator>
            {filteredCount}
            {intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.processScope.count',
            })}
          </CountNumerator>
          {' / '}
          {totalCount}
          {intl.formatMessage({
            id: 'wbsItemSearchFilterPanel.processScope.count',
          })}
        </CountLabel>
      </FooterContainer>
    </Container>
  )
}

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  background: colorPalette.monotone[0],
  padding: '10px',
  width: '100%',
})

const SearchInput = styled(InputBase)({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  padding: '4px 8px',
  marginBottom: '8px',
  fontSize: '14px',
})

const OptionsList = styled('div')({
  flex: 1,
  overflowY: 'auto',
})

const OptionItem = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  cursor: 'pointer',
  background: colorPalette.monotone[0],
  '&:hover': {
    background: colorPalette.monotone[1],
  },
  borderRadius: '4px',
  padding: '2px 0',
})

const OptionItemContent = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
})

const ExpandToggle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  cursor: 'pointer',
  flexShrink: 0,
})

const OptionLabel = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  margin: '2px 10px',
  flex: 1,
  minWidth: 0,
})

const EllipsisSpan = styled('span')({
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

type EllipsisTextProps = {
  children: string
  className?: string
}

const EllipsisText = memo(({ children, className }: EllipsisTextProps) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)

  const handleMouseEnter = useCallback(() => {
    if (ref.current && ref.current.scrollWidth > ref.current.clientWidth) {
      setOpen(true)
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    setOpen(false)
  }, [])

  return (
    <MuiTooltip
      title={children}
      open={open}
      disableHoverListener={true}
      disableFocusListener={true}
      disableTouchListener={true}
    >
      <EllipsisSpan
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={className}
      >
        {children}
      </EllipsisSpan>
    </MuiTooltip>
  )
})

const OptionName = styled(EllipsisText)({
  fontSize: '12px',
  lineHeight: 1.43,
  color: colorPalette.monotone[4],
})

const ExpandTogglePlaceholder = styled('div')({
  width: '20px',
  height: '20px',
  flexShrink: 0,
})

// アイコンの sx は行ごと・再描画ごとに再シリアライズされるため、静的な styled に切り出す
const ExpandLessIcon = styled(ExpandLessRounded)({
  fontSize: 16,
  color: colorPalette.monotone[4],
  transform: 'rotate(180deg)',
})

const ExpandMoreIcon = styled(ExpandMoreRounded)({
  fontSize: 16,
  color: colorPalette.monotone[4],
  transform: 'rotate(-90deg)',
})

// 共有 Checkbox（MuiCheckbox: padding 3px + marginRight 8px）と同じ寸法・余白を
// 踏襲した表示専用アイコン。上下の 3px が行高（ROW_HEIGHT=26）を担っている
const CheckIcon = styled('img')({
  width: 21,
  height: 20,
  margin: '3px 11px 3px 3px',
  flexShrink: 0,
})

const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
})

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const Spinner = styled('div')({
  width: '28px',
  height: '28px',
  border: `3px solid ${colorPalette.monotone[2]}`,
  borderTop: `3px solid ${colorPalette.skyBlue[7]}`,
  borderRadius: '50%',
  animation: `${spin} 0.8s linear infinite`,
})

const FooterContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  marginTop: '8px',
})

const CountLabel = styled('span')({
  fontSize: '12px',
  color: colorPalette.monotone[3],
})

const CountNumerator = styled('span')({
  fontWeight: 'bold',
  color: colorPalette.monotone[5],
})

type TreeNodeRowProps = {
  node: TreeSelectOption
  depth: number
  isExpanded: boolean
  isSelected: boolean
  onToggleItem: (value: string) => void
  onToggleExpand: (e: React.MouseEvent, value: string) => void
}

const TreeNodeRow = memo(
  ({
    node,
    depth,
    isExpanded,
    isSelected,
    onToggleItem,
    onToggleExpand,
  }: TreeNodeRowProps) => {
    const hasChildren = node.children.length > 0
    const stickyTop = depth * ROW_HEIGHT

    return (
      <OptionItem
        onClick={() => onToggleItem(node.value)}
        style={{
          paddingLeft: `${depth * 20 + 2}px`,
          ...(hasChildren && {
            position: 'sticky',
            top: `${stickyTop}px`,
            zIndex: 100 - depth,
            paddingTop: 0,
            paddingBottom: 0,
          }),
        }}
      >
        <OptionItemContent>
          {hasChildren ? (
            <ExpandToggle onClick={e => onToggleExpand(e, node.value)}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ExpandToggle>
          ) : (
            <ExpandTogglePlaceholder />
          )}
          {/* 行全体がクリック対象のため、チェックは表示専用の img で描画し
              MUI Checkbox（ButtonBase/Ripple）のレンダリングコストを避ける */}
          <CheckIcon
            src={isSelected ? checkboxActiveMIcon : checkboxInactiveMIcon}
          />
          {node.iconUrl && <img src={node.iconUrl} />}
          <OptionLabel>
            <OptionName>{node.name}</OptionName>
          </OptionLabel>
        </OptionItemContent>
      </OptionItem>
    )
  }
)
